<template>
  <!-- ------------------------------------------------------------>
  <!-- MAIN CONTAINER -->
  <!-- -------------- -->
  <div class="row mt-3 mb-3">
    <!-- ------------------ -->
    <!--   delete confirm   -->
    <!-- ------------------ -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ------------------------------------------------------- -->
    <!-- LEFT COL (BUTTONS)  -->
    <!-- ------------------- -->
    <div class="col-md-1 mt-1 px-3">
      handleClose();
      <CharacterAdjustmentsButtonsPanel
        :canAdd="canAdd"
        @close="handleClose()"
      />
    </div>
    <!-- ---------------------------------------------------------->
    <!--  RIGHT COL (DATA) -->
    <!-- ------------------ -->
    <div class="col-xl-11">
      <div class="row">
        <p class="bb-sheet-title">{{ adj_category }} ({{ computeAdjType }})</p>
      </div>
      <div class="container-fluid mt-3 mb-2">
        <!-- ----------------------------------------------------- -->
        <!-- headings -->
        <!-- ----------------------------------------------------- -->
        <div class="row">
          <div class="col-1 text-center">
            <p class="bb-hdg mt-2 mb-3">Adj</p>
          </div>
          <div class="col-4 text-center">
            <p class="bb-hdg mt-2 mb-3">Adj Reason</p>
          </div>
          <div class="bb-col-counter text-center">
            <p class="bb-hdg mt-2 mb-3">Adj Value</p>
          </div>
          <div class="col-3 text-center">
            <p class="bb-hdg mt-2 mb-3">Linked Item</p>
          </div>
          <div class="col-auto ms-3">
            <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
          </div>
          <div class="col-auto ms-3">
            <p class="bb-hdg mt-2 mb-3">&nbsp;</p>
          </div>
        </div>
        <!-- ----------------------------------------------------- -->
        <!-- data rows -->
        <!-- ----------------------------------------------------- -->
        <template
          v-for="(a, idx) in local_adjs"
          :key="a.id || idx"
        >
          <div class="row mt-3 mb-3 align-items-center">
            <!-- ----------------------------------------------------- -->
            <!-- ref -->
            <!-- ----------------------------------------------------- -->
            <div class="col-1 text-center">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="idx + 1"
                    :id="`adj-ref-${a.id || 'new'}-${idx}`"
                  />
                  <label
                    class="readonly bb-float-label"
                    :for="`adj-ref-${a.id || 'new'}-${idx}`"
                    >Ref
                  </label>
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- reason -->
            <!-- ----------------------------------------------------- -->
            <div class="col-4">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    :readonly="!a.editable"
                    class="form-control"
                    maxlength="60"
                    :value="a.adj_reason"
                    v-bind:class="{
                      'is-invalid': getErrorMessage(idx, 'adj_reason'),
                    }"
                    @input="onReasonInput($event, idx)"
                    :id="`adj-reason-${a.id || 'new'}-${idx}`"
                  />
                  <label
                    class="readonly bb-float-label"
                    :for="`adj-reason-${a.id || 'new'}-${idx}`"
                    >Reason
                  </label>
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- adj value -->
            <!-- ----------------------------------------------------- -->
            <div class="bb-col-counter">
              <div class="form-group">
                <div
                  class="form-floating"
                  v-if="!a.editable || !a.adj_reason"
                >
                  <input
                    readonly
                    class="form-control text-center"
                    :value="adjsTotDisplay(a.adj_value)"
                    :id="`adj-value-${a.id || 'new'}-${idx}`"
                    disabled
                  />
                  <label
                    class="readonly bb-float-label"
                    :for="`adj-value-${a.id || 'new'}-${idx}`"
                    >Value
                  </label>
                </div>
                <div v-else>
                  <CounterComponent
                    :pass_ref="`counter-adj-value${idx}`"
                    :pass_label="`Value`"
                    :pass_count="a.adj_value"
                    :pass_min="min_adj"
                    :pass_max="max_adj"
                    :pass_digits="calcPassDigits"
                    :pass_skip_zero="true"
                    v-bind:class="{
                      'is-invalid': getErrorMessage(idx, 'adj_value'),
                    }"
                  />
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- character item (type + name in dropdown) -->
            <!-- -----------------------------------------------------  ? getItemName(local_adjs[idx]) : ''"-->
            <div class="col-3">
              <div class="form-group">
                <div
                  class="form-floating"
                  v-if="!a.editable"
                >
                  <input
                    readonly
                    class="form-control"
                    :value="getItemName(local_adjs[idx])"
                    :id="`adj-item-${a.id || 'new'}-${idx}`"
                    disabled
                  />
                  <label
                    class="readonly bb-float-label"
                    :for="`adj-item-${a.id || 'new'}-${idx}`"
                    >Item
                  </label>
                </div>
                <div
                  v-else
                  class="form-floating"
                >
                  <v-select
                    :options="itemOptions"
                    label="display"
                    :reduce="reduceItem"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_adjs[idx].item_id"
                    :id="`adj-item-${a.id || 'new'}-${idx}`"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="(value) => onItemSelect(value, idx)"
                  >
                    <!-- Selected value shows just the item name (option is the option object itself) -->
                    <template #selected-option="{ name }">
                      <span>{{ name || '' }}</span>
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label optional"
                    for="item"
                    >Item</label
                  >
                </div>
              </div>
            </div>
            <!-- ----------------------------------------------------- -->
            <!-- undo / delete buttons grouped to avoid wrapping -->
            <!-- ----------------------------------------------------- -->
            <div class="col-auto d-flex align-items-center gap-2">
              <button
                :id="`adj-undo-${a.id || 'new'}-${idx}`"
                type="button"
                :disabled="!a.editable || !changedFlags[idx]"
                @click="undoChanges(idx)"
                class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
              >
                <ToolTip
                  text="Undo Adjustment"
                  position="left"
                >
                  <img
                    :src="undoImg"
                    height="48"
                    width="48"
                    alt="undo image"
                  />
                </ToolTip>
              </button>
              <button
                :id="`adj-delete-${a.id || 'new'}-${idx}`"
                type="button"
                :disabled="!a.editable"
                @click="confirmDeletion(idx)"
                class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
              >
                <ToolTip
                  text="Delete Adjustment"
                  position="left"
                >
                  <img
                    :src="deleteImg"
                    height="48"
                    width="48"
                    alt="delete image"
                  />
                </ToolTip>
              </button>
            </div>
          </div>
          <!-- ----------------------------------------------------- -->
          <!-- row-level errors -->
          <!-- ----------------------------------------------------- -->
          <div
            v-if="hasRowError(idx)"
            class="row mt-1 mb-3"
          >
            <div class="col-1"></div>
            <div class="col-11">
              <div class="bb-error">
                <p class="mb-0">
                  <span
                    v-for="(msg, i) in getRowErrors(idx)"
                    :key="i"
                    >{{ msg }}</span
                  >
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import { mapMutations } from 'vuex'

import CharacterAdjustmentsButtonsPanel from '../components/CharacterAdjustmentsButtonsPanel.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import CounterComponent from '../components/Counter.vue'
import ToolTip from '../components/ToolTip.vue'

import createAdjDetailEntry from '../helpers/createAdjDetailEntry.js'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import isAlphanumeric from '../helpers/isAlphanumeric.js'
import isEmpty from '../helpers/isEmpty.js'

import deleteImg from '../assets/images/bbDelete48.png'
import undoImg from '../assets/images/bbUndo.png'

export default {
  name: 'CharacterAdjustmentsView',
  //========================================
  // props
  //-------
  props: {
    applied: {
      type: Boolean,
      required: true,
    },
    adjs: {
      type: Array,
      required: false,
      default: () => [],
    },
    adj_category: {
      type: String,
      required: true,
    },
    adj_type: {
      type: String,
      required: false,
      default: '',
    },
    adj_label: {
      type: String,
      required: false,
      default: '',
    },
    min_adj: {
      type: Number,
      required: true,
    },
    max_adj: {
      type: Number,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    CounterComponent,
    ToolTip,
    CharacterAdjustmentsButtonsPanel,
    ConfirmDialog,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      deleteImg,
      undoImg,
      // delete confirmation
      delete: false,
      //
      local_character: null,
      // create local copy of adjustments received via props
      local_adjs: [],
      // listeners
      addAdjustmentRowListener: null,
      adjValueListeners: [],
      errors: [],
      // for undo detection
      baselineAdjs: [],
      // for change detection
      originalAdjs: [],
      // items: ['Sword', 'Shield', 'Potion', 'Armor'], // Example items
      item: null,
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    adjs: {
      handler(newAdjs) {
        //-----------------------------------------------------
        // copy received adjs & cap first letter of adj_reason
        //-----------------------------------------------------
        this.local_adjs = newAdjs.map((a) => ({
          ...a,
          editable:
            this.adj_category === 'skill'
              ? (a.editable ?? a.source === 'character')
              : (a.editable ?? true),
          adj_reason: capitaliseFirstLetter(a.adj_reason || ''),
          // keep a per-row item id for the v-select
          // supports both legacy object shape (character_item) and new id-based item field
          item_id:
            typeof a.character_item === 'number' ||
            typeof a.character_item === 'string'
              ? a.character_item
              : (a.character_item?.id ?? null),
        }))
        //--------------------------------
        // save values for undo detection
        //--------------------------------
        this.setBaselineAdjs()
        //---------------------------------------
        // add empty row where initially no adjs
        //---------------------------------------
        if (newAdjs.length === 0) {
          this.addAdjustmentRow()
        }
      },
      immediate: true,
      deep: true,
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    defaultAdjValue() {
      return this.max_adj == 99 ? 10 : 1
    },
    computeAdjType() {
      if (!isEmpty(this.adj_label)) {
        return this.adj_label
      }
      return this.adj_type
    },
    calcPassDigits() {
      const pass = this.max_adj > 9 ? 2 : 1
      return pass
    },
    hasInvalidAdj() {
      return this.local_adjs.some(
        (a) =>
          isEmpty(a.adj_reason) || isEmpty(a.adj_value) || a.adj_value === 0,
      )
    },
    canAdd() {
      return !this.hasInvalidAdj
    },
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    // options for item dropdown: "type - name"
    itemOptions() {
      if (!Array.isArray(this.items)) return []
      const sorted = [...this.items].sort((a, b) => {
        const typeA = (a.type || '').toString().toLowerCase()
        const typeB = (b.type || '').toString().toLowerCase()
        if (typeA < typeB) return -1
        if (typeA > typeB) return 1
        const nameA = (a.name || '').toString().toLowerCase()
        const nameB = (b.name || '').toString().toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
      return sorted.map((i) => ({
        ...i,
        display: [i.type, i.name].filter(Boolean).join(' - '),
      }))
    },
    changedFlags() {
      return this.local_adjs.map((a, idx) => {
        const orig = this.baselineAdjs[idx]
        if (!orig || !a) return false
        const currentItemId = a.item_id ?? a.character_item?.id ?? null
        const origItemId = orig.item_id ?? null
        return (
          a.adj_reason !== orig.adj_reason ||
          a.adj_value !== orig.adj_value ||
          currentItemId !== origItemId
        )
      })
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //=============================================
    // vuex functions
    //----------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
    }),
    //========================================================
    // on item select
    //----------------
    onItemSelect(itemId, idx) {
      const selected = this.items.find((c) => c.id === itemId) || null
      const name = selected?.name ?? null
      console.log(
        'onItemSelect id = ',
        itemId,
        ' name = ',
        name,
        ' idx = ',
        idx,
      )

      // persist both id and full item object on this adjustment row
      if (this.local_adjs[idx]) {
        this.local_adjs[idx].item_id = itemId || null
        this.local_adjs[idx].character_item = selected
      }
    },
    //========================================================
    // reduce item option -> id (for v-select)
    //-----------------------------------------
    reduceItem(option) {
      return option?.id ?? null
    },
    //=============================================
    // has row error
    //---------------
    hasRowError(idx) {
      return this.errors && this.errors.some((e) => e.id === idx)
    },
    //=============================================
    // get error message
    //-------------------
    getErrorMessage(idx, field) {
      if (!this.errors || !Array.isArray(this.errors)) return ''
      const e = this.errors.find((er) => er.id === idx && er.field === field)
      return e ? e.message : ''
    },
    //=============================================
    // get row errors
    //----------------
    getRowErrors(idx) {
      if (!this.errors) return []
      // return readable messages
      return this.errors.filter((e) => e.id === idx).map((e) => e.message)
    },
    //=============================================
    // get item name (handles nulls / ids / objects)
    //-----------------------------------------------
    getItemName(adj) {
      if (!adj) return ''

      const characterItem = adj.character_item

      // 1. Full item object with name
      if (characterItem && typeof characterItem === 'object') {
        if (characterItem.name) {
          return characterItem.name
        }
        if (characterItem.item && characterItem.item.name) {
          return characterItem.item.name
        }
      }

      // 2. Stored as plain id (number/string) or via item_id
      const itemId =
        typeof characterItem === 'number' || typeof characterItem === 'string'
          ? characterItem
          : (adj.item_id ?? null)

      if (itemId == null || !Array.isArray(this.items)) {
        return ''
      }

      const found = this.items.find((i) => i.id === itemId)
      return found && found.name ? found.name : ''
    },
    //=============================================
    // adjs total display
    //--------------------
    adjsTotDisplay(val) {
      const numericValue = Number(val)

      if (Number.isNaN(numericValue)) {
        return val
      }

      const displayValue = Number.isInteger(numericValue)
        ? numericValue
        : numericValue.toFixed(1)

      return numericValue > 0 ? `+${displayValue}` : `${displayValue}`
    },
    //=============================================
    // on reason input
    //-----------------
    onReasonInput(event, idx) {
      const val = (event.target.value || '').slice(0, 60)
      // Capitalize first letter
      this.local_adjs[idx].adj_reason = capitaliseFirstLetter(val)
      // clear any validation error for this row
      this.errors = this.errors.filter((e) => e.id !== idx)
    },
    //=============================================
    // set view
    //----------
    setView(view) {
      this.setCurrentView(view)
    },
    //=============================================
    // undo changes
    //--------------
    undoChanges(idx) {
      const orig = this.baselineAdjs[idx]
      if (orig) {
        this.local_adjs[idx].adj_reason = orig.adj_reason
        this.local_adjs[idx].adj_value = orig.adj_value
        // restore linked item selection from baseline snapshot
        this.local_adjs[idx].item_id = orig.item_id ?? null
      }
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //=============================================
    // add adjustment row
    //--------------------
    addAdjustmentRow() {
      const newAdj = createAdjDetailEntry({
        id: null,
        name: this.adj_type,
        adj_reason: '',
        adj_value: 0, //this.defaultAdjValue,
        item: null,
        editable: true,
        rune: '',
        source: 'character',
        applied: this.applied,
      })
      // push new adjustment to local array
      this.local_adjs.push(newAdj)
      // create listener for new row
      this.createAdjListener(this.local_adjs.length - 1)
    },
    //=============================================
    // set original adjs for change detection
    //----------------------------------------
    setOriginalAdjs() {
      // console.log('----------------------------------')
      // console.log('setOriginalAdjs called');
      // console.log('local_adjs at setOriginalAdjs = ', this.local_adjs);
      this.originalAdjs = this.local_adjs.map((a) => ({
        id: a.id,
        adj_reason: a.adj_reason,
        adj_value: a.adj_value,
        // track linked item id for change detection
        item_id: a.item_id ?? a.character_item?.id ?? null,
      }))
      // console.log('Original adjs set to:', this.originalAdjs);
      // console.log('originalAdjs length:', this.originalAdjs.length);
    },
    //=============================================
    // set baseline adjs for undo detection
    //---------------------------------------
    setBaselineAdjs() {
      // console.log('----------------------------------')
      // console.log('setBaselineAdjs called');
      this.baselineAdjs = this.local_adjs.map((a) => ({
        id: a.id,
        adj_reason: a.adj_reason,
        adj_value: a.adj_value,
        // track linked item id so undo restores selection
        item_id: a.item_id ?? a.character_item?.id ?? null,
      }))
      // console.log('Baseline adjs set to:', this.baselineAdjs);
      // console.log('baselineAdjs length:', this.baselineAdjs.length);
    },
    //=============================================
    // create adj listener
    //---------------------
    createAdjListener(idx) {
      // define event name
      const eventName = `counter-adj-value${idx}`
      //  unregister previous listener if any
      const prevListener = this.adjValueListeners[idx]
      if (prevListener) {
        this.emitter.off(eventName, prevListener)
      }
      // create listener function
      const listener = (return_count) => {
        this.local_adjs[idx].adj_value = return_count
        // clear any validation error for this row when the value changes
        this.errors = this.errors.filter((e) => e.id !== idx)
      }
      // save listener
      this.adjValueListeners[idx] = listener
      // register listener
      this.emitter.on(eventName, listener)
    },
    //=============================================
    // confirm deletion
    //------------------
    async confirmDeletion(idx) {
      this.delete = true
      const ok = await this.$refs.confirmDialog.show({
        title: 'Delete Adjustment',
        message: 'Confirm Deletion',
        subject: 'Adjustment ' + (idx + 1),
        okButton: 'OK',
        cancelButton: 'Cancel',
      })
      if (ok) {
        //-----------------------
        // remove row from array
        //-----------------------
        this.local_adjs.splice(idx, 1)
        //-----------------------------------
        // add empty row if all adjs deleted
        //-----------------------------------
        if (this.local_adjs.length === 0) {
          this.addAdjustmentRow()
        }
        //------------------------------
        // reset baseline adjs for undo
        //------------------------------
        this.setBaselineAdjs()
      } else {
        this.delete = false
      }
    },
    //=============================================
    // unregister all listeners
    //--------------------------
    unregisterAllListeners() {
      // loop saved listeners (not local_adjs which might be empty)
      this.adjValueListeners.forEach((handler, idx) => {
        if (handler) {
          const eventName = `counter-adj-value${idx}`
          // console.log('Unregistering:', eventName);
          this.emitter.off(eventName, handler)
        }
      })
      // unregister static listeners
      if (this.addAdjustmentRow)
        this.emitter.off('add-adjustment-row', this.addAdjustmentRowListener)
      //
      this.baselineAdjs = []
      this.adjValueListeners = []

      // console.log('All listeners unregistered');
    },
    //=============================================
    // validate adjs
    //---------------
    validateAdjs() {
      let errors = []
      //------------------------
      // check for empty fields
      //------------------------
      this.local_adjs.forEach((adj, idx) => {
        if (
          !adj.adj_reason &&
          (adj.adj_value !== 0 || adj.character_item !== null)
        ) {
          errors.push({
            id: idx,
            field: 'adj_reason',
            message: window.bbUiErrorMsg('Reason required'),
          })
        } else if ((adj.adj_reason || '').length > 60) {
          errors.push({
            id: idx,
            field: 'adj_reason',
            message: window.bbUiErrorMsg('Reason cannot exceed 60 characters'),
          })
        } else if (adj.adj_reason && adj.adj_value == 0) {
          errors.push({
            id: idx,
            field: 'adj_value',
            message: window.bbUiErrorMsg(
              'Value cannot be 0 when reason provided',
            ),
          })
        }
      })
      //--------------------------
      // check for unique reasons
      //--------------------------
      const reasonMap = {}
      this.local_adjs.forEach((adj, idx) => {
        if (adj.adj_reason) {
          reasonMap[adj.adj_reason] = (reasonMap[adj.adj_reason] || []).concat(
            idx,
          )
        }
      })
      Object.keys(reasonMap).forEach((reason) => {
        const idxs = reasonMap[reason]
        if (idxs.length > 1) {
          // push one error object per dup row index
          idxs.forEach((i) => {
            errors.push({
              id: i,
              field: 'adj_reason',
              message: window.bbUiErrorMsg('Reasons must be unique'),
            })
          })
        }
      })
      //---------------
      // return errors
      //---------------
      return errors
    },
    //=============================================
    // handle close
    //--------------
    handleClose() {
      // console.log('----------------------------------')
      // console.log('handleClose called');
      //---------------
      // validate adjs
      //----------------
      let errors = this.validateAdjs(this.local_adjs)
      //-------------
      // show errors
      //-------------
      if (errors.length > 0) {
        this.errors = errors
        return
      }
      // console.log('1. local_adjs = ', this.local_adjs);
      // console.log('2. baselineAdjs = ', this.baselineAdjs);
      //------------------
      // clear blank rows
      //------------------
      this.local_adjs = this.local_adjs.filter(
        (adj) => adj.adj_reason || adj.adj_value !== 0,
      )
      // console.log('3. local_adjs = ', this.local_adjs);
      // console.log('4. local_adjs cnt = ', this.local_adjs.length);

      // console.log('4. originalAdjs = ', this.originalAdjs);
      // console.log('5. originalAdjs cnt = ', this.originalAdjs.length);
      //---------------------------
      // determine if changes made
      //---------------------------
      const changed =
        // check if array lengths differ (items added/removed)
        this.local_adjs.length !== this.originalAdjs.length ||
        // check if any existing items have changed content
        this.local_adjs.some((a, idx) => {
          const orig = this.originalAdjs[idx]
          if (!orig || !a) return true // Safety check
          const currentItemId = a.item_id ?? a.character_item?.id ?? null
          const origItemId = orig.item_id ?? null
          return (
            a.adj_reason !== orig.adj_reason ||
            a.adj_value !== orig.adj_value ||
            currentItemId !== origItemId
          )
        })
      // console.log('Changes detected = ', changed);
      //---------------
      // where changes
      //---------------
      if (changed) {
        // normalise item field to be the character item id on exit
        const payloadAdjs = this.local_adjs.map((a) => {
          const itemId = a.item_id ?? a.character_item?.id ?? null
          return {
            ...a,
            character_item: itemId,
          }
        })
        this.$emit('save-skill-adjs', {
          category: this.adj_category,
          type: this.adj_type,
          data: payloadAdjs,
        })
      } else {
        //------------------
        // where no changes
        //------------------
        this.$emit('close-skill-adjs')
      }
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //----------
    // set view
    //----------
    if (this.adj_category === 'skill') {
      if (this.applied) {
        this.setView('character_adjustments_applied')
      } else {
        this.setView('character_adjustments_optional')
      }
    } else {
      this.setView('character_adjustments')
    }
    //----------------------------------
    // save values for change detection
    //----------------------------------
    this.setOriginalAdjs()
    //--------------------------------
    // save values for undo detection
    //--------------------------------
    this.setBaselineAdjs()
    //--------------------------------
    // register dynamic adj listeners
    //--------------------------------
    this.local_adjs.forEach((_, idx) => {
      this.createAdjListener(idx)
    })
    //---------------------------------
    // add new adjustment row listener
    //---------------------------------
    this.addAdjustmentRowListener = () => {
      this.addAdjustmentRow()
    }
    this.emitter.on('add-adjustment-row', this.addAdjustmentRowListener)
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    this.unregisterAllListeners()
  },
}
</script>
